export interface ParsedAiMessageContent {
  displayText: string;
  speakableText: string;
  media: string[];
}

/** Matches `IMAGE (https://...)`, `- PDF (url)`, etc. */
const AI_MEDIA_REFERENCE_PATTERN =
  /(?:^|[\n\r])[\s\-*]*?(IMAGE|VIDEO|PDF)\s*\(\s*(https?:\/\/[^)\s]+)\s*\)/gi;

const INLINE_MEDIA_REFERENCE_PATTERN = /\b(IMAGE|VIDEO|PDF)\s*\(\s*(https?:\/\/[^)\s]+)\s*\)/gi;

const URL_PATTERN = /https?:\/\/[^\s)\]'"]+/gi;
const EMAIL_PATTERN = /\b[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\b/gi;

const FORMATTED_PHONE_PATTERNS = [
  /\+[\d\s().-]{7,20}/g,
  /\(\d{2,4}\)[\s.-]?\d{3,4}[\s.-]?\d{3,4}/g,
  /\b\d{3}[-.\s]\d{3}[-.\s]\d{4}\b/g,
  /\b\d{4}[-.\s]\d{4}\b/g,
] as const;

/** Standalone digit runs such as 22213546, 343434, 3434343. */
const STANDALONE_DIGIT_SEQUENCE = /\b\d{6,15}\b/g;

/**
 * Extracts AI resource references from message text, returns cleaned display
 * copy (no raw URLs) and speakable copy for text-to-speech.
 */
export function parseAiMessageContent(text: string): ParsedAiMessageContent {
  if (!text?.trim()) {
    return { displayText: '', speakableText: '', media: [] };
  }

  const media: string[] = [];

  const collectMedia = (url: string): string => {
    const trimmedUrl = url.trim();
    if (!media.includes(trimmedUrl)) {
      media.push(trimmedUrl);
    }
    return '';
  };

  let displayText = text.replace(AI_MEDIA_REFERENCE_PATTERN, (_match, _type, url: string) =>
    collectMedia(url),
  );

  displayText = displayText.replace(INLINE_MEDIA_REFERENCE_PATTERN, (_match, _type, url: string) =>
    collectMedia(url),
  );

  displayText = displayText.replace(URL_PATTERN, '');
  displayText = cleanupMessageText(displayText);

  return {
    displayText,
    speakableText: buildSpeakableText(displayText, media.length > 0),
    media,
  };
}

function buildSpeakableText(displayText: string, hasMedia: boolean): string {
  let text = markdownToSpeech(displayText);

  text = text.replace(EMAIL_PATTERN, '');
  text = text.replace(URL_PATTERN, '');
  text = replacePhoneNumbersForSpeech(text);

  if (hasMedia) {
    text = text.replace(/here are some helpful resources:?/gi, '');
  }

  return cleanupMessageText(text);
}

/** Sentence-ending punctuation (Latin + Arabic) — a line ending in one already pauses. */
const LINE_END_PUNCTUATION = /[.!?:;,…؟،]$/;

/**
 * Speech-only: drops markdown symbols and turns each line into its own sentence so
 * voices pause between list items ("1. Car\n2. Taxi" → "1, Car.\n2, Taxi.").
 * Voices ignore line breaks, so without this a list is read as one run.
 */
function markdownToSpeech(text: string): string {
  return text
    .split(/\r?\n/)
    .map(rawLine => {
      let line = rawLine.trim();

      // Horizontal rule: --- / *** / ___
      if (/^([-*_])(\s*\1){2,}$/.test(line)) {
        return '';
      }

      line = line.replace(/^#{1,6}\s+/, ''); // # Heading
      line = line.replace(/^>\s?/, ''); // > quote

      const numbered = line.match(/^([\d٠-٩]{1,3})[.)]\s+(.+)$/);
      if (numbered) {
        line = `${numbered[1]}, ${numbered[2]}`; // 1. Car → 1, Car
      } else {
        line = line.replace(/^[-*+•●▪◦–]\s+/, ''); // - Car / • Car → Car
      }

      line = stripInlineMarkdown(line).trim();
      if (line && !LINE_END_PUNCTUATION.test(line)) {
        line += '.';
      }
      return line;
    })
    .filter(Boolean)
    .join('\n');
}

/** **bold**, __bold__, *italic*, _italic_, ~~strike~~, `code`, [text](url) → plain words. */
function stripInlineMarkdown(line: string): string {
  return (
    line
      // URLs are already removed upstream, so a link arrives as [text] or [text]().
      .replace(/\[([^\]]+)\](?:\([^)]*\))?/g, '$1')
      .replace(/(\*\*|__)(?=\S)(.+?)(?<=\S)\1/g, '$2')
      .replace(/~~(?=\S)(.+?)(?<=\S)~~/g, '$1')
      .replace(/`([^`]+)`/g, '$1')
      // Single * / _ only as wrappers, so trip_id or 2*3 stay intact.
      .replace(/(^|[\s(])\*(?=\S)(.+?)(?<=\S)\*(?=[\s).,!?:;]|$)/g, '$1$2')
      .replace(/(^|[\s(])_(?=\S)(.+?)(?<=\S)_(?=[\s).,!?:;]|$)/g, '$1$2')
      // Leftover asterisks (except 2*3) and standalone # are never meant to be spoken.
      .replace(/(?<!\d)\*+|\*+(?!\d)/g, '')
      .replace(/(^|\s)#+(?=\s|$)/g, '$1')
  );
}

/** Converts phone numbers to spaced digits so TTS reads them one by one. */
function replacePhoneNumbersForSpeech(text: string): string {
  let result = text;

  for (const pattern of FORMATTED_PHONE_PATTERNS) {
    result = result.replace(pattern, match => digitsToSpokenDigits(match));
  }

  result = result.replace(STANDALONE_DIGIT_SEQUENCE, match => digitsToSpokenDigits(match));

  return result;
}

function digitsToSpokenDigits(value: string): string {
  const digits = value.replace(/\D/g, '');

  if (digits.length < 6) {
    return value;
  }

  // Keep surrounding spaces the pattern captured, or the number runs into the next word.
  const leading = value.match(/^\s*/)?.[0] ?? '';
  const trailing = value.match(/\s*$/)?.[0] ?? '';
  return leading + digits.split('').join(' ') + trailing;
}

function cleanupMessageText(text: string): string {
  return text
    .replace(/\(\s*\)/g, '')
    .replace(/^[ \t]*[-*][ \t]*$/gm, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Returns text optimized for speech synthesis. */
export function getSpeakableAiMessageText(text: string): string {
  return parseAiMessageContent(text).speakableText;
}
